import { useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { IngredientPicker } from "@/components/bills/IngredientPicker";
import { fmtQty } from "@/lib/billsLanguage";

const input = "w-full h-11 rounded-md border border-[#E4E0D8] px-3 text-[15px]";
const btn = "h-11 px-4 rounded-md text-[14px] font-semibold";
const KINDS = [["SOURCE", "Quantity or unit"], ["INGREDIENT", "Ingredient"], ["CONVERSION", "Pack size"]];

// Correction of a CONFIRMED line. Amounts, tax, bill number and vendor are never editable here.
export const LineCorrection = ({ ln, busy, error, onSubmit, onCancel }) => {
  const [kind, setKind] = useState(ln.needs_information ? "CONVERSION" : "SOURCE");
  const [f, setF] = useState({ item_quantity: ln.purchased_quantity ?? "", item_unit: ln.purchased_unit ?? "", multiplier: "", ingredient_id: null, ingredient_name: null });
  const [reason, setReason] = useState("");
  const [review, setReview] = useState(false);
  const actionKey = useRef(null);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const body = () => kind === "SOURCE"
    ? { kind, item_quantity: Number(f.item_quantity) || undefined, item_unit: f.item_unit || undefined }
    : kind === "INGREDIENT" ? { kind, ingredient_id: f.ingredient_id }
      : { kind, base_uom: ln.ingredient_base_uom, multiplier: Number(f.multiplier) || undefined };
  const ready = reason.trim() && (kind !== "INGREDIENT" || f.ingredient_id) && (kind !== "CONVERSION" || Number(f.multiplier) > 0);
  const t = `correction-${ln.line_index}`;
  return (
    <div className="mt-3 space-y-3 rounded-md border border-amber-300 bg-amber-50 p-3" data-testid={t}>
      {!ln.needs_information && (
        <div className="flex flex-wrap gap-2" role="tablist">
          {KINDS.map(([k, label]) => (
            <button key={k} type="button" onClick={() => { setKind(k); setReview(false); }} data-testid={`${t}-kind-${k}`}
              className={`${btn} border ${kind === k ? "border-[var(--action)] bg-white text-[var(--action)]" : "border-[#E4E0D8] bg-white"}`}>{label}</button>
          ))}
        </div>
      )}
      {kind === "SOURCE" && (
        <div className="grid grid-cols-2 gap-2">
          <input inputMode="decimal" value={f.item_quantity} onChange={set("item_quantity")} aria-label="Quantity" className={input} data-testid={`${t}-qty`} />
          <input value={f.item_unit} onChange={set("item_unit")} aria-label="Unit" className={input} data-testid={`${t}-unit`} />
        </div>
      )}
      {kind === "INGREDIENT" && (f.ingredient_id
        ? <p className="text-[14px]" data-testid={`${t}-picked`}>New ingredient: <b>{f.ingredient_name}</b></p>
        : <IngredientPicker lineId={`corr-${ln.line_index}`} busy={busy} onPick={(id, name) => setF((x) => ({ ...x, ingredient_id: id, ingredient_name: name || id }))} />)}
      {kind === "CONVERSION" && (
        <label className="block text-[14px]">How many {ln.ingredient_base_uom} in 1 {ln.purchased_unit}?
          <input inputMode="decimal" value={f.multiplier} onChange={set("multiplier")} className={`${input} mt-1`} data-testid={`${t}-multiplier`} />
        </label>
      )}
      <textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why is this being corrected?" rows={2}
        className="w-full rounded-md border border-[#E4E0D8] px-3 py-2 text-[15px]" data-testid={`${t}-reason`} />
      {review ? (
        <div className="space-y-2" data-testid={`${t}-review`}>
          <p className="text-[13px] text-amber-900">This changes the inventory quantity for this item. The bill amount stays the same, and Inventory will receive the correction.</p>
          <button type="button" disabled={busy} onClick={() => onSubmit({ line_id: ln.line_id, reason: reason.trim(), expected_revision: ln.correction_revision ?? 0, ...body() }, actionKey.current)}
            className={`${btn} text-white w-full`} style={{ backgroundColor: "var(--action)" }} data-testid={`${t}-confirm`}>
            {busy ? <Loader2 size={16} className="animate-spin mx-auto" /> : "Confirm correction"}</button>
        </div>
      ) : (
        <div className="flex gap-2">
          <button type="button" disabled={!ready} onClick={() => { actionKey.current = crypto.randomUUID(); setReview(true); }} className={`${btn} text-white disabled:opacity-50`}
            style={{ backgroundColor: "var(--action)" }} data-testid={`${t}-next`}>Review correction</button>
          {onCancel && <button type="button" onClick={onCancel} className={`${btn} border border-[#E4E0D8] bg-white`} data-testid={`${t}-cancel`}>Cancel</button>}
        </div>
      )}
      {error && <p role="alert" className="text-[13px] text-red-700" data-testid={`${t}-error`}>{error}</p>}
    </div>
  );
};

export const CorrectionHistory = ({ ln }) => !ln.corrections?.length ? null : (
  <div className="mt-3 rounded-md bg-archival-soft px-3 py-2 text-[13px] space-y-1" data-testid={`correction-history-${ln.line_index}`}>
    {ln.original && <p>Originally confirmed: <span className="font-mono">{fmtQty(ln.original.raw_quantity)} {ln.original.raw_unit}{ln.original.base_quantity != null ? ` → ${fmtQty(ln.original.base_quantity)} ${ln.original.base_unit}` : ""}</span>{ln.original.ingredient_name ? ` · ${ln.original.ingredient_name}` : ""}</p>}
    {ln.corrections.map((c) => (
      <p key={c.correction_id}>Corrected: <span className="font-mono">{fmtQty(c.new.raw_quantity)} {c.new.raw_unit}</span> · Reason: {c.reason}</p>
    ))}
  </div>
);
